/**
 * Folder-first document administration — one implementation for the estate.
 *
 * Every app that owns documents grew the same screen: a flat list of every
 * document with a category filter. People Policies got a folder-first
 * version on 2026-07-31 (folder cards → open a folder → its contents), and
 * it obviously belonged everywhere rather than being copied a seventh time.
 *
 * Renders the whole surface: folder grid, breadcrumbs, document tiles
 * (via doc-tiles), preview (via doc-viewer), upload / edit / delete /
 * move, the audience-condition builder, and the folder manager.
 *
 *   import { mountDocAdmin } from '@isg/ui-kit';
 *   const admin = mountDocAdmin(container, {
 *     appSlug: 'quality',
 *     apiBase: '/admin/documents',          // api-client relative
 *     readerBase: '/api/quality/documents', // absolute; for <img>/viewer URLs
 *   });
 *   // admin.destroy() on teardown
 *
 * ── Two rules this component exists to enforce ────────────────────────
 * 1. Its CSS ships WITH it (`import '../../css/documents.css'`). The
 *    modal/badge classes used to live in each app's own stylesheet, so a
 *    page copied between apps rendered its modals INLINE in the page flow
 *    with no error anywhere — exactly how this bug reached production.
 * 2. A document names its folder in ONE field (`folderField`, default
 *    'category'). Apps disagree about the column name, and about whether
 *    the value is a category slug (six apps) or a folder id (the
 *    intranet) — so identity is normalised to a string here and both
 *    shapes navigate with the same code.
 *
 * Nesting: folders may carry `parent_slug` or `parent_id`. Apps whose
 * folders are flat simply never set one, and the same code renders a
 * single level. Breadcrumbs walk the parent chain with a cycle guard, so
 * adopting nesting later needs no change — which HR did on 2026-08-17,
 * with `features.folderNesting` turning on the parent control in the
 * folder manager. Rendering was already here; only authoring is new.
 *
 * 🔴 An ORPHAN — a folder whose parent isn't in the list — is drawn at the
 * ROOT, not skipped. It is not a root (it has a parent) and no card owns
 * it (its parent isn't drawn), so skipping means the folder and every
 * document in it vanish with nothing logged, and they don't appear under
 * Unfiled either because their slug still resolves. See `drawnParentOf`.
 */
import '../../css/documents.css';
import { api } from '../services/api.js';
import { getIcon } from './navigation.js';
import { openViewer } from './doc-viewer.js';
import {
  renderDocList, wireDocTiles, docViewToggle, wireDocViewToggle,
  readDocViewMode, writeDocViewMode,
} from './doc-tiles.js';
import { showFolderManager, defaultCountries, conditionTypes } from './folder-manager.js';
import { formatFileSize } from '../utils/formatters.js';
import { t } from '../i18n/index.js';

const UNFILED = '__unfiled__';

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// Apps disagree on field names; read all the spellings we've seen rather
// than making every caller write a mapping table.
const isLink = (d) => (d.source_type || 'file') === 'link';

const get = {
  fileName: (d) => d.file_name || d.fileName || d.filename || '',
  fileSize: (d) => d.file_size ?? d.fileSize ?? 0,
  mimeType: (d) => d.mime_type || d.mimeType || '',
  version:  (d) => d.updated_at || d.updatedAt || d.created_at || d.createdAt || '0',
  active:   (d) => d.active !== false && d.is_active !== false,
};

// The country list and the condition-type list are built at RENDER time
// (in folder-manager.js, shared with it) — a module-scope array of labels
// would freeze them in whatever language was active when the module loaded.

function fileTypeLabel(mime) {
  if (!mime) return t('filetypes.file');
  if (mime === 'application/pdf') return 'PDF'; // i18n-ignore: a format name, identical in every locale
  if (mime.includes('wordprocessingml') || mime === 'application/msword') return t('filetypes.word');
  if (mime.includes('spreadsheetml') || mime === 'application/vnd.ms-excel') return t('filetypes.excel');
  if (mime.includes('presentationml') || mime === 'application/vnd.ms-powerpoint') return t('filetypes.powerpoint');
  if (mime.startsWith('image/')) return t('filetypes.image');
  return t('filetypes.file');
}

/**
 * @param {HTMLElement} container
 * @param {Object}   cfg
 * @param {string}   cfg.appSlug            — namespaces the card/list view preference
 * @param {string}   cfg.apiBase            — admin CRUD base, api-client relative
 *                                            (default '/admin/documents'). GET list,
 *                                            POST upload, PUT/DELETE `${apiBase}/<id>`.
 * @param {string}   [cfg.foldersBase]      — folder CRUD base (default `${apiBase}/categories`)
 * @param {string}   cfg.readerBase         — ABSOLUTE base for file URLs, e.g.
 *                                            '/api/quality/documents'. Used to build
 *                                            /<id>/thumbnail|view|preview.
 * @param {Object}   [cfg.urls]             — override individual URL builders:
 *                                            {thumbnail(doc), view(doc), preview(doc)}
 * @param {string}   [cfg.folderField]      — document field holding the folder slug
 *                                            (default 'category')
 * @param {string}   [cfg.title]            — page banner heading
 * @param {Object}   [cfg.labels]           — {folder:'Folder', folders:'Folders', ...}
 * @param {Object}   [cfg.features]         — {conditions, activeToggle, folderManager,
 *                                            upload, search, delete} — default true
 *                                            except conditions (auto: on if lookups
 *                                            given); {links, folderNesting} default
 *                                            false and need backend support
 * @param {Object}   [cfg.lookups]          — {companies:'/admin/companies',
 *                                            departments:'/admin/departments'}
 * @param {Array}    [cfg.extraFields]      — per-app fields, see below
 * @param {Function} [cfg.onError]          — (err, action) => void (default alert)
 *
 * extraFields entries:
 *   { key, label, type: 'checkbox'|'text'|'select', options?, default?,
 *     help?, readOnly?, badge?: {label, icon, when?} }
 * They are rendered in BOTH modals, sent as form fields on upload and as
 * JSON keys on edit, and optionally drawn as a badge on the tile.
 * `readOnly: true` gives the badge WITHOUT a form control and without
 * sending the key — for state the app owns elsewhere (marketing's
 * `published`, which moves via its own publish/unpublish endpoints). A
 * checkbox that silently does nothing is worse than no checkbox.
 */
export function mountDocAdmin(container, cfg = {}) {
  const {
    appSlug = 'app',
    apiBase = '/admin/documents',
    readerBase = '',
    folderField = 'category',
    title = t('docs.title'),
    extraFields = [],
    lookups = {},
    countries = defaultCountries(),
    onError,
  } = cfg;

  const foldersBase = cfg.foldersBase || `${apiBase}/categories`;
  // Mount time, not module scope: t() must run once the locale is known.
  // `folders` asks for count 2 so Czech gets the NOMINATIVE plural ("Složky",
  // the `few` form) — `other` is the genitive ("Složek"), which is right
  // after a number and wrong on a button.
  const labels = Object.assign({
    folder: t('docs.folder', { count: 1 }), folders: t('docs.folder', { count: 2 }),
    upload: t('docs.upload_document'), uploadHere: t('docs.upload_here'),
    allFolders: t('docs.all_folders'), unfiled: t('docs.unfiled'),
    emptyTitle: t('docs.folder_empty'),
    emptyHint: t('docs.empty_hint'),
    noFolders: t('docs.no_folders'),
    externalLabel: t('docs.visibility_external_label'),
    externalHint: t('docs.visibility_external_hint'),
    internalLabel: t('docs.visibility_internal_label'),
    internalHint: t('docs.visibility_internal_hint'),
    internalBadge: t('docs.internal_badge'),
  }, cfg.labels || {});

  const features = Object.assign({
    conditions: Boolean(lookups.companies || lookups.departments || lookups.combined),
    activeToggle: true,
    folderManager: true,
    upload: true,
    search: true,
    // Marketing has no DELETE route for documents at all — an app says so
    // once here rather than the component offering a button that 405s.
    delete: true,
    // Link documents (a URL to SharePoint/Teams/a supplier portal instead
    // of an upload). OFF by default: an app's backend must accept
    // `link_url` and store `source_type`, and offering the choice where it
    // doesn't is an affordance that 400s.
    links: false,
    // Subfolders. RENDERING nesting has always been automatic — a folder
    // carrying `parent_slug`/`parent_id` is drawn under its parent with no
    // configuration at all. This flag is about CREATING it: whether the
    // folder manager offers a parent control. OFF by default for the same
    // reason as `links` — an app's backend must accept and validate
    // `parent_slug`, and a control that always 400s is worse than none.
    folderNesting: false,
    // Internal or external — does this document reach the intranet, or stay
    // inside the app that owns it? OFF by default: an app's backend must
    // carry `is_external` and filter its reader on it, and a control that
    // silently does nothing is worse than no control. Same reasoning as
    // `links` and `folderNesting`.
    //
    // ⚠️ Two states, not a checkbox. "External ☐" leaves the reader to work
    // out what unticking it does, and the answer — "this disappears from the
    // intranet for everyone" — is too consequential to infer from an empty
    // box. The radios say the outcome in words.
    visibility: false,
    // Drag-to-reorder the documents inside a folder. OFF by default for the
    // same reason as `links` and `folderNesting`: it needs
    // `PUT <apiBase>/reorder` accepting {ids:[…]}, and a drag that silently
    // 404s on drop is worse than a list that plainly cannot be dragged.
    reorder: false,
    // Folder audiences (BETA-00231) — an audience on the FOLDER, inherited
    // by documents in it that carry none of their own. OFF by default for
    // the same reason as the rest: the backend must accept `conditions` on
    // the folder routes, and only apps on `isg_common.documents` do.
    folderAudience: false,
  }, cfg.features || {});

  // Per-app row actions (marketing's Publish / Unpublish live on their own
  // endpoints rather than as a PUT field, and that pattern will recur).
  //   { label(doc), icon?, title?, when?(doc), run(doc) -> Promise }
  const extraActions = cfg.extraActions || [];

  const url = Object.assign({
    thumbnail: (d) => `${readerBase}/${d.id}/thumbnail?v=${encodeURIComponent(get.version(d))}`,
    view:      (d) => `${readerBase}/${d.id}/view?v=${encodeURIComponent(get.version(d))}`,
    preview:   (d) => `${readerBase}/${d.id}/preview?v=${encodeURIComponent(get.version(d))}`,
  }, cfg.urls || {});

  // `action` stays an English identifier — it is what an app's onError
  // receives. `message` is the translated fallback shown to the reader.
  const fail = (err, action, message) => {
    if (onError) return onError(err, action);
    // eslint-disable-next-line no-alert
    alert((err && err.message) || message || t('common.something_went_wrong'));
  };

  // ── state ──────────────────────────────────────────────────────────
  let documents = [];
  let folders = [];
  let companies = [];
  let departments = [];
  let loading = true;
  let currentFolder = null;          // null = root; slug | UNFILED
  let search = '';
  let modal = null;                  // null | 'upload' | 'edit'
  let editDoc = null;
  let busy = false;
  let viewMode = readDocViewMode(`${appSlug}.adminDocsView`, 'grid');
  let destroyed = false;

  // form state (shared by both modals; `form.conditions` is the builder's)
  let form = {};
  let condType = 'company';
  let condValue = '';
  // The chosen File, held OUTSIDE the DOM: adding a condition (or hitting
  // Save) re-renders the modal, which replaces <input type="file"> and
  // silently empties its FileList. Reading the input at submit time would
  // then "lose" a file the user can still see named in the dialog.
  let pendingFile = null;

  const escHandler = (e) => {
    if (e.key === 'Escape' && modal) { modal = null; editDoc = null; render(); }
  };
  document.addEventListener('keydown', escHandler);

  // ── folder identity ────────────────────────────────────────────────
  // Most apps file documents under a category SLUG; the intranet uses a
  // folder_id integer. Both are handled by normalising identity to a
  // string, so the same navigation code serves either shape.
  const fid = (f) => String(f.slug != null ? f.slug : f.id);
  const parentOf = (f) => {
    if (f.parent_slug != null) return String(f.parent_slug);
    if (f.parent_id != null) return String(f.parent_id);
    return null;
  };
  const docFolder = (d) => (d[folderField] == null ? null : String(d[folderField]));

  const activeFolders = () => folders.filter((f) => f.is_active !== false);
  const folderById = (id) => folders.find((f) => fid(f) === String(id)) || null;

  // 🔴 The parent a folder is DRAWN under — its own if that folder is in
  // the list, otherwise none. A `parent_slug` naming a folder that isn't
  // here (deleted out from under it, or filtered out upstream as
  // inactive) makes an ORPHAN, and an orphan is rendered by NOTHING: it
  // is not a root because it has a parent, and no card owns it because
  // its parent isn't drawn. The folder and every document in it silently
  // disappear — and they don't show up as Unfiled either, because their
  // slug still resolves. Promoting them to the root makes that
  // unrepresentable here regardless of what the server sent.
  const drawnParentOf = (f) => {
    const p = parentOf(f);
    return p && folders.some((x) => fid(x) === p) ? p : null;
  };
  const childrenOf = (id) => folders.filter((f) => {
    const p = drawnParentOf(f);
    return id === null ? !p : p === String(id);
  });
  const docsIn = (id) => documents.filter((d) => docFolder(d) === String(id));

  // Documents in a folder AND everything beneath it. A parent folder that
  // showed only its DIRECT children read as empty whenever its content
  // lived one level down, which is the normal shape once nesting is used.
  // Cycle-safe: a folder re-parented under its own descendant would
  // otherwise recurse for ever.
  function docCountDeep(id, seen) {
    seen = seen || new Set();
    const key = String(id);
    if (seen.has(key)) return 0;
    seen.add(key);
    return childrenOf(key).reduce(
      (n, child) => n + docCountDeep(fid(child), seen),
      docsIn(key).length,
    );
  }

  // Documents whose folder no longer resolves — deleted or renamed outside
  // the guard. They must never become invisible.
  function unfiledDocs() {
    const known = new Set(folders.map(fid));
    return documents.filter((d) => !known.has(docFolder(d)));
  }

  function ancestors(id) {
    const chain = [];
    let f = folderById(id);
    let guard = 0;   // cycle-safe: a folder re-parented under its own child
    while (f && guard++ < 20) {
      chain.unshift(f);
      const p = parentOf(f);
      f = p ? folderById(p) : null;
    }
    return chain;
  }

  function matchesSearch(d) {
    if (!search) return true;
    const q = search.toLowerCase();
    return (String(d.title || '').toLowerCase().includes(q)
      || get.fileName(d).toLowerCase().includes(q)
      || String(d.description || '').toLowerCase().includes(q));
  }

  function conditionLabel(c) {
    if (c.type === 'company') {
      const m = companies.find((x) => String(x.id) === String(c.value) || x.slug === c.value);
      return m ? m.name : c.value;
    }
    if (c.type === 'country') {
      const m = countries.find((x) => x.value === c.value);
      return m ? m.label : c.value;
    }
    if (c.type === 'department') {
      const m = departments.find((x) => String(x.id) === String(c.value) || x.name === c.value);
      return m ? m.name : c.value;
    }
    return c.value;
  }

  // The condition TYPE is a stored slug (company/country/department); the
  // badge shows its translated name, falling back to the slug for a type
  // this component does not know.
  function conditionTypeLabel(type) {
    const known = conditionTypes().find((ct) => ct.value === type);
    return known ? known.label : type;
  }

  function visibilityField() {
    if (!features.visibility) return '';
    const ext = form.is_external !== false;
    const opt = (val, on, label, hint) => `
      <label class="isgdoc-vis${on ? ' is-on' : ''}">
        <input type="radio" name="isgdoc-vis" data-vis="${val}" ${on ? 'checked' : ''} />
        <span><strong>${esc(label)}</strong><small>${esc(hint)}</small></span>
      </label>`;
    return `<div class="isgdoc-field">
      <label>${esc(t('docs.visibility'))}</label>
      <div class="isgdoc-vis-group">
        ${opt('1', ext, labels.externalLabel, labels.externalHint)}
        ${opt('0', !ext, labels.internalLabel, labels.internalHint)}
      </div>
    </div>`;
  }

  function badgesFor(d) {
    const vis = (features.visibility && d.is_external === false)
      ? `<span class="cond-badge flag">${getIcon('lock', 12)} ${esc(labels.internalBadge)}</span>`
      : '';
    return vis + extraFields
      .filter((f) => f.badge && (f.badge.when ? f.badge.when(d) : Boolean(d[f.key])))
      .map((f) => `<span class="cond-badge flag">${f.badge.icon ? getIcon(f.badge.icon, 12) : ''} ${esc(f.badge.label)}</span>`)
      .join('');
  }

  // ── rendering ──────────────────────────────────────────────────────
  function bannerActions(inFolder) {
    const btn = (id, icon, text) => `
      <button class="btn btn-sm" id="${id}" style="display:inline-flex;align-items:center;gap:0.375rem;background:rgba(255,255,255,0.2);color:#fff;border:1px solid rgba(255,255,255,0.3);">
        ${getIcon(icon, 16)} ${esc(text)}
      </button>`;
    let html = '';
    // Inside a folder the manager button stays only when subfolders are
    // available — that is where "add a subfolder HERE" is asked for, and
    // making the user go back to the root to create one is the same
    // journey "Upload here" exists to remove.
    if (features.folderManager && (!inFolder || features.folderNesting)) {
      html += btn('isgdoc-folders', 'folder', labels.folders);
    }
    if (features.upload && currentFolder !== UNFILED) {
      html += btn('isgdoc-upload', 'upload', inFolder ? labels.uploadHere : labels.upload);
    }
    return html ? `<div style="display:flex;align-items:center;gap:0.5rem;flex-shrink:0;">${html}</div>` : '';
  }

  function folderCard(f) {
    const direct = docsIn(fid(f)).length;
    const count = docCountDeep(fid(f));
    const kids = childrenOf(fid(f)).length;
    const bits = [t('docs.document_count', { count })];
    // Say where they are when they aren't all at this level, so the
    // rollup can't be mistaken for the folder's own contents.
    if (count > direct) bits.push(t('docs.in_subfolders', { count: count - direct }));
    if (kids) bits.push(t('docs.folder_count', { count: kids }));
    if (f.is_active === false) bits.push(t('docs.inactive_folder'));
    return `
      <div class="card card-hover isgdoc-folder${f.is_active === false ? ' is-inactive' : ''}" data-folder="${esc(fid(f))}">
        <div class="isgdoc-folder-head">
          <div class="isgdoc-folder-icon">${getIcon('folder', 22)}</div>
          <div style="min-width:0;">
            <div class="isgdoc-folder-name">${esc(f.name)}</div>
            <div class="isgdoc-folder-count">${esc(bits.join(' · '))}</div>
          </div>
        </div>
        ${f.description ? `<p class="isgdoc-folder-desc">${esc(f.description)}</p>` : ''}
      </div>`;
  }

  function unfiledCard(n) {
    return `
      <div class="card card-hover isgdoc-folder is-unfiled" data-folder="${UNFILED}">
        <div class="isgdoc-folder-head">
          <div class="isgdoc-folder-icon">${getIcon('folder', 22)}</div>
          <div>
            <div class="isgdoc-folder-name">${esc(labels.unfiled)}</div>
            <div class="isgdoc-folder-count">${esc(t('docs.unfiled_count', { count: n }))}</div>
          </div>
        </div>
        <p class="isgdoc-folder-desc">${esc(t('docs.orphaned'))}</p>
      </div>`;
  }

  function skeletonGrid() {
    return `<div class="isgdoc-folder-grid">${Array.from({ length: 8 }, () => `
      <div class="card" style="padding:1.25rem;">
        <div class="skeleton" style="height:1.25rem;width:9rem;border-radius:0.25rem;margin-bottom:0.5rem;"></div>
        <div class="skeleton" style="height:0.875rem;width:12rem;border-radius:0.25rem;"></div>
      </div>`).join('')}</div>`;
  }

  function renderRoot() {
    const unfiled = unfiledDocs();
    const roots = childrenOf(null);
    const stats = t('docs.root_stats', {
      count: documents.length,
      folders: t('docs.folder_count', { count: activeFolders().length }),
    });

    let html = `
      <div class="page-banner">
        <div class="page-banner-inner">
          <div><h1>${esc(title)}</h1><p>${esc(stats)}</p></div>
          ${bannerActions(false)}
        </div>
      </div>`;

    if (loading) return html + skeletonGrid();

    if (!roots.length && !unfiled.length) {
      return `${html}
        <div class="isgdoc-empty">
          <div class="isgdoc-empty-icon">${getIcon('folder', 48)}</div>
          <p class="isgdoc-empty-title">${esc(labels.noFolders)}</p>
        </div>`;
    }

    html += `<div class="isgdoc-folder-grid">
      ${roots.map(folderCard).join('')}
      ${unfiled.length ? unfiledCard(unfiled.length) : ''}
    </div>`;
    return html;
  }

  function renderFolder() {
    const isUnfiled = currentFolder === UNFILED;
    const folder = isUnfiled ? null : folderById(currentFolder);
    const chain = isUnfiled ? [] : ancestors(currentFolder);
    const subfolders = isUnfiled ? [] : childrenOf(currentFolder);
    const all = isUnfiled ? unfiledDocs() : docsIn(currentFolder);
    const docs = all.filter(matchesSearch);
    const name = isUnfiled ? labels.unfiled : (folder ? folder.name : currentFolder);

    const crumbs = [
      `<a href="#" data-crumb="">${getIcon('folder', 14)} ${esc(labels.allFolders)}</a>`,
      ...chain.slice(0, -1).map((f) => `<span class="sep">/</span><a href="#" data-crumb="${esc(fid(f))}">${esc(f.name)}</a>`),
      `<span class="sep">/</span><span class="current">${esc(name)}</span>`,
    ].join('');

    let html = `
      <div class="page-banner">
        <div class="page-banner-inner">
          <div style="min-width:0;">
            <div class="isgdoc-crumbs">${crumbs}</div>
            <h1>${esc(name)}</h1>
            <p>${folder && folder.description
                  ? esc(folder.description)
                  : esc(t('docs.document_count', { count: all.length }))}${
                 !isUnfiled && docCountDeep(currentFolder) > all.length
                   ? ` · ${esc(t('docs.including_subfolders', { count: docCountDeep(currentFolder) }))}` : ''}</p>
          </div>
          ${bannerActions(true)}
        </div>
      </div>`;

    if (subfolders.length) {
      html += `<div class="isgdoc-folder-grid" style="margin-bottom:1.5rem;">${subfolders.map(folderCard).join('')}</div>`;
    }

    if (!all.length) {
      return `${html}
        <div class="isgdoc-empty">
          <div class="isgdoc-empty-icon">${getIcon('file-text', 48)}</div>
          <p class="isgdoc-empty-title">${esc(labels.emptyTitle)}</p>
          <p class="isgdoc-empty-hint">${esc(labels.emptyHint)}</p>
        </div>`;
    }

    html += `<div class="isgdoc-toolbar">
      ${features.search ? `<div class="isgdoc-search" style="max-width:320px;flex:1;">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
        <input type="text" id="isgdoc-search" value="${esc(search)}" placeholder="${esc(t('docs.search_placeholder'))}" />
      </div>` : '<div class="isgdoc-toolbar-spacer"></div>'}
      ${docViewToggle(viewMode)}
    </div>`;

    if (!docs.length) {
      html += `<div class="isgdoc-empty">
        <p class="isgdoc-empty-title">${esc(t('docs.no_match', { query: search }))}</p>
      </div>`;
      return html;
    }

    // ⚠️ Reordering a FILTERED list is meaningless — the drop position maps
    // to a neighbour the user cannot see, and persisting 0..n-1 over a
    // subset silently rewrites the order of everything hidden. So the
    // handles disappear while a search is active, rather than lying.
    const canReorder = features.reorder && !search && docs.length > 1;

    html += `<div id="isgdoc-doclist"${canReorder ? ' data-reorderable="1"' : ''}>`;
    html += renderDocList(docs, {
      mode: viewMode,
      attrs: (d) => (canReorder ? ` draggable="true" data-reorder-id="${d.id}"` : ''),
      thumbUrl: (d) => (isLink(d) ? null : url.thumbnail(d)),
      title: (d) => d.title || get.fileName(d),
      subtitle: (d) => d.description || get.fileName(d),
      dimmed: (d) => !get.active(d),
      meta: (d) => {
        const conds = d.conditions || [];
        return `<div style="display:flex;align-items:center;gap:0.375rem;flex-wrap:wrap;">
          ${isLink(d)
            ? `<span class="cond-badge flag">${getIcon('external-link', 12)} ${esc(t('docs.link'))}</span>`
            : `<span style="font-size:0.75rem;color:var(--text-tertiary);">${esc(fileTypeLabel(get.mimeType(d)))}</span>
               <span style="font-size:0.75rem;color:var(--text-tertiary);">${formatFileSize(get.fileSize(d))}</span>`}
          ${features.conditions ? (conds.length
            ? conds.map((c) => `<span class="cond-badge ${esc(c.type)}">${esc(conditionTypeLabel(c.type))}: ${esc(conditionLabel(c))}</span>`).join('')
            : `<span class="cond-badge global">${getIcon('check-circle', 12)} ${esc(t('docs.global'))}</span>`) : ''}
          ${badgesFor(d)}
        </div>`;
      },
      actions: (d) => `
        ${features.activeToggle ? `<label class="isgdoc-check" title="${esc(get.active(d) ? t('common.active') : t('common.inactive'))}">
          <input type="checkbox" data-toggle-active="${d.id}" ${get.active(d) ? 'checked' : ''} style="width:1rem;height:1rem;cursor:pointer;" />
        </label>` : ''}
        ${extraActions.map((a, i) => (a.when && !a.when(d)) ? '' :
          `<button class="btn btn-sm" data-extra-action="${i}" data-doc="${d.id}">${esc(a.label(d))}</button>`).join('')}
        <div class="inline-actions">
          <button data-edit-doc="${d.id}" title="${esc(t('docs.edit_move'))}" style="color:var(--brand-600);">${getIcon('edit', 16)}</button>
          ${features.delete ? `<button data-delete-doc="${d.id}" title="${esc(t('common.delete'))}" style="color:var(--red-500);">${getIcon('trash-2', 16)}</button>` : ''}
        </div>`,
    });
    html += '</div>';
    return html;
  }

  function extraFieldHtml(f) {
    const v = form[f.key];
    if (f.type === 'checkbox') {
      return `<label class="isgdoc-check">
        <input type="checkbox" data-field="${esc(f.key)}" ${v ? 'checked' : ''} />
        ${esc(f.label)}
      </label>`;
    }
    if (f.type === 'select') {
      return `<div class="isgdoc-field">
        <label>${esc(f.label)}</label>
        <select data-field="${esc(f.key)}">
          ${(f.options || []).map((o) => `<option value="${esc(o.value)}"${String(v) === String(o.value) ? ' selected' : ''}>${esc(o.label)}</option>`).join('')}
        </select>
        ${f.help ? `<p class="isgdoc-audience-hint">${esc(f.help)}</p>` : ''}
      </div>`;
    }
    return `<div class="isgdoc-field">
      <label>${esc(f.label)}</label>
      <input type="text" data-field="${esc(f.key)}" value="${esc(v == null ? '' : v)}" />
      ${f.help ? `<p class="isgdoc-audience-hint">${esc(f.help)}</p>` : ''}
    </div>`;
  }

  function audienceBuilder() {
    if (!features.conditions) return '';
    // 🔴 Write the SLUG (or department NAME), never the numeric id.
    //
    // This used to prefer `c.id`, on the reasoning that `check_conditions`
    // matches both tolerantly. It does — but only because
    // `isg_common.documents.conditions._equivalents()` builds
    // `rayfast-uk -> {'10','rayfast-uk'}` by reading the LIVE companies
    // table on every check. That makes reader visibility depend on a company
    // row keeping its numeric id for ever:
    //
    //   · delete and recreate a company (a routine-looking admin action) and
    //     every document conditioned on the old id goes dark SILENTLY —
    //     nothing errors, the documents just stop appearing;
    //   · `_equivalents()` swallows its own exceptions and returns EMPTY maps
    //     on any isg_core hiccup, and an empty map matches no id at all.
    //
    // `company_id` is the slug estate-wide (same rule the helpdesk ticket
    // form was corrected to in July). A slug is the stable identifier; the
    // id is an implementation detail of one table.
    //
    // ⚠️ 106 existing rows were normalised on 2026-08-07 —
    // `/opt/isg/docs/scripts/normalise_condition_company_ids.py`. Reverting
    // this line quietly starts producing the old encoding again.
    const opts = condType === 'company'
      ? companies.map((c) => ({ value: String(c.slug || c.id), label: c.name }))
      : condType === 'country'
        ? countries
        : departments.map((d) => ({ value: String(d.name || d.id), label: d.name }));
    const conds = form.conditions || [];
    return `
      <div class="isgdoc-audience">
        <label style="display:block;font-size:0.875rem;font-weight:500;color:var(--text-primary);margin-bottom:0.5rem;">${esc(t('docs.audience'))}</label>
        <p class="isgdoc-audience-hint">${esc(t('docs.audience_hint'))}</p>
        <div class="isgdoc-audience-row">
          <select id="isgdoc-cond-type">
            ${conditionTypes().map((ct) => `<option value="${ct.value}"${condType === ct.value ? ' selected' : ''}>${esc(ct.label)}</option>`).join('')}
          </select>
          <select id="isgdoc-cond-value" class="value">
            <option value="">${esc(t('common.select_placeholder'))}</option>
            ${opts.map((o) => `<option value="${esc(o.value)}"${condValue === o.value ? ' selected' : ''}>${esc(o.label)}</option>`).join('')}
          </select>
          <button type="button" class="btn btn-sm" id="isgdoc-cond-add" style="display:inline-flex;align-items:center;gap:0.25rem;">
            ${getIcon('plus', 14)} ${esc(t('common.add'))}
          </button>
        </div>
        <div class="isgdoc-audience-list">
          ${conds.length === 0
            ? `<span class="cond-badge global">${getIcon('check-circle', 12)} ${esc(t('docs.everyone'))}</span>`
            : conds.map((c, i) => `<span class="cond-badge ${esc(c.type)}">${esc(conditionTypeLabel(c.type))}: ${esc(conditionLabel(c))}
                <button type="button" data-cond-remove="${i}">&times;</button></span>`).join('')}
        </div>
      </div>`;
  }

  // Every selectable folder, in tree order, indented by depth.
  //
  // 🔴 A flat list of names is WRONG once folders nest: "Templates" under
  // Onboarding and "Templates" under Leaving are two different folders
  // rendering as two identical options, and picking the wrong one files
  // the document somewhere nobody will look. The indent is the only thing
  // on screen that tells them apart.
  //
  // Inactive folders are not offered — nothing new should be filed into
  // one — but their children still are, promoted to the parent's indent
  // rather than dropped with it.
  function folderOptions() {
    const out = [];
    const walk = (parent, depth, seen) => {
      childrenOf(parent).forEach((f) => {
        const id = fid(f);
        if (seen.has(id)) return;        // cycle-safe
        seen.add(id);
        const live = f.is_active !== false;
        if (live) out.push({ value: id, depth, name: f.name });
        walk(id, live ? depth + 1 : depth, seen);
      });
    };
    walk(null, 0, new Set());
    return out;
  }

  function folderSelect() {
    const opts = folderOptions();
    // ⚠️ The document's CURRENT folder must be in the list even when it is
    // inactive. Otherwise the select falls back to its first option and
    // pressing Save — without touching the control — moves the document
    // to whatever happens to sort first. Same shape of bug as the org
    // chart's "Drawn under" select, which was caught on screen.
    const current = String(form.folder ?? '');
    if (current && !opts.some((o) => o.value === current)) {
      const f = folderById(current);
      opts.unshift({
        value: current,
        depth: 0,
        name: f
          ? t('docs.folder_option_inactive', { name: f.name })
          : t('docs.folder_option_missing', { id: current }),
      });
    }
    // Non-breaking spaces: a plain space collapses inside <option>
    // and the indent — the only thing distinguishing two folders with
    // the same name — silently vanishes.
    const indent = (d) => (d ? `${'\u00A0'.repeat(d * 4)}\u2514\u00A0` : '');
    return `<div class="isgdoc-field">
      <label>${esc(modal === 'edit' ? t('docs.folder_label_move', { label: labels.folder }) : labels.folder)}</label>
      <select id="isgdoc-folder-select">
        ${opts.map((o) => `<option value="${esc(o.value)}"${current === o.value ? ' selected' : ''}>${indent(o.depth)}${esc(o.name)}</option>`).join('')}
      </select>
    </div>`;
  }

  function modalHtml() {
    if (!modal) return '';
    const isUpload = modal === 'upload';
    const heading = isUpload
      ? `${labels.upload}${currentFolder && currentFolder !== UNFILED ? ` — ${esc(folderById(currentFolder)?.name || currentFolder)}` : ''}`
      : esc(t('docs.edit_title'));
    return `
      <div class="isgdoc-modal-overlay" id="isgdoc-overlay">
        <div class="isgdoc-modal isgdoc-modal-lg">
          <h3 style="font-weight:600;margin-bottom:1rem;">${heading}</h3>
          <form class="isgdoc-form" id="isgdoc-form">
            ${isUpload && features.links ? `<div class="isgdoc-field">
              <label>${esc(t('docs.source'))}</label>
              <div class="isgdoc-checks">
                <label class="isgdoc-check"><input type="radio" name="isgdoc-src" value="file" ${form.source === 'link' ? '' : 'checked'} /> ${esc(t('docs.source_file'))}</label>
                <label class="isgdoc-check"><input type="radio" name="isgdoc-src" value="link" ${form.source === 'link' ? 'checked' : ''} /> ${esc(t('docs.source_link'))}</label>
              </div>
            </div>` : ''}
            ${isUpload && form.source === 'link' ? `<div class="isgdoc-field">
              <label>${esc(t('docs.link_label'))}</label>
              <input type="text" data-field="link_url" value="${esc(form.link_url || '')}"
                placeholder="${esc(t('docs.link_placeholder'))}" />
              <p class="isgdoc-audience-hint">${esc(t('docs.link_hint'))}</p>
            </div>` : ''}
            ${isUpload && form.source !== 'link' ? `<div class="isgdoc-field">
              <label>${esc(t('docs.file_label'))}</label>
              <input type="file" id="isgdoc-file" />
              ${pendingFile ? `<p class="isgdoc-audience-hint">${esc(t('docs.selected_file', { name: pendingFile.name }))}</p>` : ''}
            </div>` : ''}
            ${!isUpload && editDoc && isLink(editDoc) ? `<div class="isgdoc-field">
              <label>${esc(t('docs.link'))}</label>
              <input type="text" data-field="link_url" value="${esc(form.link_url || '')}" />
            </div>` : ''}
            <div class="isgdoc-field">
              <label>${esc(t('docs.doc_title_label'))}</label>
              <input type="text" data-field="title" required
                placeholder="${esc(t('docs.doc_title_placeholder'))}" value="${esc(form.title || '')}" />
            </div>
            <div class="isgdoc-field">
              <label>${esc(t('docs.description'))}</label>
              <textarea data-field="description" rows="2" placeholder="${esc(t('docs.description_placeholder'))}">${esc(form.description || '')}</textarea>
            </div>
            ${folderSelect()}
            <div class="isgdoc-checks">
              ${!isUpload && features.activeToggle ? `<label class="isgdoc-check">
                <input type="checkbox" data-field="active" ${form.active ? 'checked' : ''} /> ${esc(t('common.active'))}
              </label>` : ''}
              ${extraFields.filter((f) => !f.readOnly && f.type === 'checkbox').map(extraFieldHtml).join('')}
            </div>
            ${extraFields.filter((f) => !f.readOnly && f.type !== 'checkbox').map(extraFieldHtml).join('')}
            ${visibilityField()}
            ${audienceBuilder()}
            <div class="isgdoc-error" id="isgdoc-error"></div>
            <div class="isgdoc-form-actions">
              <button type="button" class="btn btn-sm" id="isgdoc-cancel">${esc(t('common.cancel'))}</button>
              <button type="submit" class="btn btn-primary btn-sm" ${busy ? 'disabled' : ''}>
                ${esc(busy ? t('common.saving') : (isUpload ? t('common.upload') : t('common.save_changes')))}
              </button>
            </div>
          </form>
        </div>
      </div>`;
  }

  // The fade-in is a VIEW TRANSITION, not a repaint. Replaying it on every
  // state tick is what made editing a document look like the page reloading.
  let lastViewKey = null;

  function render() {
    if (destroyed) return;
    const viewKey = `${currentFolder}`;
    const fresh = viewKey !== lastViewKey;
    lastViewKey = viewKey;
    container.innerHTML = `<div class="main-content-inner${fresh ? ' animate-fade-in' : ''}">
      ${currentFolder === null ? renderRoot() : renderFolder()}
      <div id="isgdoc-modal-host">${modalHtml()}</div>
    </div>`;
    wire();
  }

  // 🔴 Re-render ONLY the modal.
  //
  // Everything the modal's own controls change — the condition type, adding
  // or removing an audience chip, the visibility radios, the busy flag —
  // leaves the folder grid and the document list completely untouched. They
  // were being rebuilt anyway, because `render()` replaces the entire
  // container: the browser threw away and re-created every node on the page,
  // replayed `animate-fade-in`, dropped the scroll position and blurred
  // whatever the user was using. From the other side of the screen that is
  // indistinguishable from a page reload — which is exactly what it was
  // reported as.
  //
  // ⚠️ Re-wiring is the trap here. `wire()` binds the shell AND the modal;
  // calling it after replacing only the modal would add a SECOND listener to
  // every folder card and row button that was never replaced, so one click
  // would fire twice. Hence the split — `wireModal()` binds only what this
  // function rebuilt.
  function renderModal() {
    if (destroyed) return;
    const host = container.querySelector('#isgdoc-modal-host');
    if (!host) { render(); return; }          // nothing to patch into yet

    // Keep the reader's place: a long form scrolled to the audience builder
    // must not jump back to the top because they changed a dropdown.
    const scrolled = host.querySelector('.isgdoc-modal');
    const top = scrolled ? scrolled.scrollTop : 0;
    const activeId = document.activeElement && host.contains(document.activeElement)
      ? document.activeElement.id
      : null;

    host.innerHTML = modalHtml();
    wireModal();

    const after = host.querySelector('.isgdoc-modal');
    if (after && top) after.scrollTop = top;
    if (activeId) {
      const back = host.querySelector(`#${CSS.escape(activeId)}`);
      if (back) back.focus({ preventScroll: true });
    }
  }

  // ── events ─────────────────────────────────────────────────────────
  //
  // Split in two because `renderModal()` rebuilds only the modal and must
  // re-bind only the modal. See the comment there.
  function wire() {
    wireShell();
    wireModal();
  }

  function wireShell() {
    container.querySelectorAll('[data-folder]').forEach((card) => {
      card.addEventListener('click', () => {
        currentFolder = card.dataset.folder;
        search = '';
        render();
      });
    });
    container.querySelectorAll('[data-crumb]').forEach((a) => {
      a.addEventListener('click', (e) => {
        e.preventDefault();
        currentFolder = a.dataset.crumb || null;
        search = '';
        render();
      });
    });

    container.querySelector('#isgdoc-folders')?.addEventListener('click', () => {
      showFolderManager({
        basePath: foldersBase,
        title: labels.folders,
        nesting: features.folderNesting,
        // Folder audiences (BETA-00231) ride on the SAME lookups the
        // document audience builder uses, so the two controls can never
        // offer different companies.
        //
        // 🔴 Gated on its OWN flag, not on `features.conditions`. An app
        // whose backend ignores `conditions` on a folder would show the
        // control, accept the rules, save without error and scope nothing —
        // a SILENT no-op, which is worse than the 400 the rest of this file
        // is written to avoid. Only apps on `isg_common.documents` honour it.
        audience: (features.folderAudience && features.conditions)
          ? { companies, departments, countries }
          : null,
        // Opened from inside a folder, "Add" means add a subfolder HERE.
        defaultParent: (currentFolder && currentFolder !== UNFILED)
          ? currentFolder : null,
        onClose: async () => { await loadFolders(); render(); },
      });
    });

    container.querySelector('#isgdoc-upload')?.addEventListener('click', () => {
      modal = 'upload';
      busy = false;
      pendingFile = null;   // never carry a file over from a previous upload
      condType = 'company';
      condValue = '';
      form = {
        title: '', description: '', conditions: [], source: 'file', link_url: '',
        // Matches the server's per-app default. An app whose documents are
        // internal by default passes `defaultExternal: false`.
        is_external: cfg.defaultExternal !== false,
        folder: (currentFolder && currentFolder !== UNFILED)
          ? currentFolder
          : (activeFolders()[0] ? fid(activeFolders()[0]) : ''),
      };
      extraFields.forEach((f) => { form[f.key] = f.default ?? (f.type === 'checkbox' ? false : ''); });
      renderModal();
    });

    const searchEl = container.querySelector('#isgdoc-search');
    searchEl?.addEventListener('input', (e) => {
      search = e.target.value;
      const pos = e.target.selectionStart;
      render();
      const el = container.querySelector('#isgdoc-search');
      if (el) { el.focus(); el.setSelectionRange(pos, pos); }
    });

    if (currentFolder !== null) {
      // Ignores clicks on the admin buttons, so Edit doesn't also open the viewer.
    wireReorder();

      wireDocTiles(container, (id) => {
        const d = documents.find((x) => String(x.id) === String(id));
        if (!d) return;
        if (isLink(d)) {
          // SharePoint and friends refuse to be framed (X-Frame-Options),
          // and the reader's own M365 session is what authorises them —
          // so hand off to a new tab rather than trying to embed.
          if (d.link_url) window.open(d.link_url, '_blank', 'noopener');
          return;
        }
        openViewer({
          url: url.view(d),
          previewUrl: url.preview(d),
          filename: get.fileName(d) || d.title,
          title: d.title,
        });
      });
      wireDocViewToggle(container, (mode) => {
        viewMode = mode;
        writeDocViewMode(`${appSlug}.adminDocsView`, mode);
        render();
      });
    }

    container.querySelectorAll('[data-edit-doc]').forEach((b) => {
      b.addEventListener('click', () => {
        const d = documents.find((x) => String(x.id) === String(b.dataset.editDoc));
        if (!d) return;
        editDoc = d;
        modal = 'edit';
        busy = false;
        condType = 'company';
        condValue = '';
        form = {
          title: d.title || '',
          description: d.description || '',
          folder: docFolder(d) || (activeFolders()[0] ? fid(activeFolders()[0]) : ''),
          link_url: d.link_url || '',
          active: get.active(d),
          is_external: d.is_external !== false,
          conditions: (d.conditions || []).map((c) => ({ type: c.type, value: c.value })),
        };
        extraFields.forEach((f) => { form[f.key] = d[f.key] ?? (f.type === 'checkbox' ? false : ''); });
        renderModal();
      });
    });

    container.querySelectorAll('[data-delete-doc]').forEach((b) => {
      b.addEventListener('click', async () => {
        const d = documents.find((x) => String(x.id) === String(b.dataset.deleteDoc));
        // eslint-disable-next-line no-alert
        if (!confirm(t('docs.confirm_delete', { title: d?.title || t('docs.this_document') }))) return;
        try {
          await api.delete(`${apiBase}/${b.dataset.deleteDoc}`);
          await loadDocuments();
          render();
        // i18n-ignore: `action` is an English identifier for the app's onError hook; the reader sees the translated message
        } catch (err) { fail(err, 'delete document', t('docs.fail_delete')); }
      });
    });

    container.querySelectorAll('[data-extra-action]').forEach((b) => {
      b.addEventListener('click', async (e) => {
        e.stopPropagation();
        const action = extraActions[Number(b.dataset.extraAction)];
        const d = documents.find((x) => String(x.id) === String(b.dataset.doc));
        if (!action || !d) return;
        b.disabled = true;
        try {
          await action.run(d);
          await loadDocuments();
          render();
        // i18n-ignore: `action` is an English identifier for the app's onError hook; the reader sees the translated message
        } catch (err) { b.disabled = false; fail(err, action.title || 'run action', t('docs.fail_action')); }
      });
    });

    container.querySelectorAll('[data-toggle-active]').forEach((cb) => {
      cb.addEventListener('change', async () => {
        try {
          await api.put(`${apiBase}/${cb.dataset.toggleActive}`, { active: cb.checked });
          await loadDocuments();
          render();
        // i18n-ignore: `action` is an English identifier for the app's onError hook; the reader sees the translated message
        } catch (err) { fail(err, 'update document', t('docs.fail_update')); render(); }
      });
    });
  }

  function wireModal() {
    container.querySelector('#isgdoc-overlay')?.addEventListener('click', (e) => {
      if (e.target.id === 'isgdoc-overlay') { modal = null; editDoc = null; renderModal(); }
    });
    container.querySelector('#isgdoc-cancel')?.addEventListener('click', () => {
      modal = null; editDoc = null; renderModal();
    });

    // Keep edits in `form` so a re-render (adding a condition) never loses them.
    container.querySelectorAll('[data-field]').forEach((el) => {
      const key = el.dataset.field;
      const evt = el.type === 'checkbox' || el.tagName === 'SELECT' ? 'change' : 'input';
      el.addEventListener(evt, () => {
        form[key] = el.type === 'checkbox' ? el.checked : el.value;
      });
    });
    container.querySelectorAll('input[name="isgdoc-src"]').forEach((r) => {
      r.addEventListener('change', () => { form.source = r.value; renderModal(); });
    });
    container.querySelectorAll('input[data-vis]').forEach((r) => {
      r.addEventListener('change', () => {
        form.is_external = r.dataset.vis === '1';
        renderModal();
      });
    });
    container.querySelector('#isgdoc-folder-select')?.addEventListener('change', (e) => {
      form.folder = e.target.value;
    });

    container.querySelector('#isgdoc-cond-type')?.addEventListener('change', (e) => {
      condType = e.target.value;
      condValue = '';
      renderModal();
    });
    container.querySelector('#isgdoc-cond-value')?.addEventListener('change', (e) => {
      condValue = e.target.value;
    });
    container.querySelector('#isgdoc-cond-add')?.addEventListener('click', () => {
      const v = container.querySelector('#isgdoc-cond-value')?.value;
      if (!v) return;
      form.conditions = form.conditions || [];
      if (form.conditions.some((c) => c.type === condType && c.value === v)) return;
      form.conditions.push({ type: condType, value: v });
      condValue = '';
      renderModal();
    });
    container.querySelectorAll('[data-cond-remove]').forEach((b) => {
      b.addEventListener('click', () => {
        form.conditions.splice(Number(b.dataset.condRemove), 1);
        renderModal();
      });
    });

    container.querySelector('#isgdoc-form')?.addEventListener('submit', onSubmit);
  }

  async function onSubmit(e) {
    e.preventDefault();
    const errEl = container.querySelector('#isgdoc-error');
    const showErr = (m) => { errEl.textContent = m; errEl.classList.add('is-shown'); };
    errEl.classList.remove('is-shown');

    if (!String(form.title || '').trim()) return showErr(t('docs.title_required'));

    busy = true;
    renderModal();

    try {
      if (modal === 'upload' && form.source === 'link') {
        const link = String(form.link_url || '').trim();
        if (!/^https?:\/\/\S+$/i.test(link)) {
          busy = false; render();
          const el = container.querySelector('#isgdoc-error');
          if (el) { el.textContent = t('docs.bad_link'); el.classList.add('is-shown'); }
          return;
        }
        const fd = new FormData();
        fd.append('link_url', link);
        fd.append('title', String(form.title).trim());
        fd.append('description', String(form.description || '').trim());
        fd.append(folderField, form.folder);
        if (features.conditions) fd.append('conditions', JSON.stringify(form.conditions || []));
        if (features.visibility) fd.append('is_external', form.is_external ? '1' : '0');
        extraFields.filter((f) => !f.readOnly).forEach((f) => {
          const v = form[f.key];
          fd.append(f.key, f.type === 'checkbox' ? (v ? '1' : '0') : String(v ?? ''));
        });
        await api.post(apiBase, fd);
      } else if (modal === 'upload') {
        if (!pendingFile) {
          busy = false;
          render();
          const el = container.querySelector('#isgdoc-error');
          if (el) { el.textContent = t('docs.file_required'); el.classList.add('is-shown'); }
          return;
        }
        const fd = new FormData();
        fd.append('file', pendingFile);
        fd.append('title', String(form.title).trim());
        fd.append('description', String(form.description || '').trim());
        fd.append(folderField, form.folder);
        if (features.conditions) fd.append('conditions', JSON.stringify(form.conditions || []));
        if (features.visibility) fd.append('is_external', form.is_external ? '1' : '0');
        extraFields.filter((f) => !f.readOnly).forEach((f) => {
          const v = form[f.key];
          fd.append(f.key, f.type === 'checkbox' ? (v ? '1' : '0') : String(v ?? ''));
        });
        await api.post(apiBase, fd);
        pendingFile = null;
      } else {
        const payload = {
          title: String(form.title).trim(),
          description: String(form.description || '').trim(),
          [folderField]: form.folder,
        };
        if (features.activeToggle) payload.active = Boolean(form.active);
        if (features.visibility) payload.is_external = Boolean(form.is_external);
        if (editDoc && isLink(editDoc)) payload.link_url = String(form.link_url || '').trim();
        if (features.conditions) payload.conditions = form.conditions || [];
        extraFields.filter((f) => !f.readOnly).forEach((f) => {
          payload[f.key] = f.type === 'checkbox' ? Boolean(form[f.key]) : form[f.key];
        });
        await api.put(`${apiBase}/${editDoc.id}`, payload);
      }
      modal = null;
      editDoc = null;
      busy = false;
      await loadDocuments();
      // Land the user where the document now lives.
      if (form.folder && currentFolder !== null && currentFolder !== form.folder) {
        currentFolder = form.folder;
      }
      render();
    } catch (err) {
      busy = false;
      render();
      const el = container.querySelector('#isgdoc-error');
      if (el) { el.textContent = (err && err.message) || t('docs.save_failed'); el.classList.add('is-shown'); }
    }
  }

  // Delegated on the container, which survives every innerHTML swap.
  container.addEventListener('change', (e) => {
    if (e.target && e.target.id === 'isgdoc-file') {
      pendingFile = (e.target.files && e.target.files[0]) || null;
    }
  });

  // ── data ───────────────────────────────────────────────────────────
  // ── drag to reorder ────────────────────────────────────────────────
  //
  // Native HTML5 drag-and-drop rather than a library: this is one vertical
  // list, and ui-kit is a `file:` dependency of nine apps — adding a
  // runtime dep here means nine `npm install`s and nine rebuilds to move a
  // row up.
  //
  // ⚠️ The order is sent ONCE, as the whole list. See the `/reorder`
  // endpoint for why a PUT per row loses races.
  let dragId = null;

  function wireReorder() {
    const list = container.querySelector('#isgdoc-doclist[data-reorderable]');
    if (!list) return;

    list.querySelectorAll('[data-reorder-id]').forEach((el) => {
      el.addEventListener('dragstart', (e) => {
        dragId = el.dataset.reorderId;
        el.classList.add('isgdoc-dragging');
        // Firefox refuses to start a drag unless something is set.
        try { e.dataTransfer.setData('text/plain', dragId); } catch (_) { /* IE-ism */ }
        e.dataTransfer.effectAllowed = 'move';
      });
      el.addEventListener('dragend', () => {
        el.classList.remove('isgdoc-dragging');
        list.querySelectorAll('.isgdoc-drop-before, .isgdoc-drop-after')
          .forEach((n) => n.classList.remove('isgdoc-drop-before', 'isgdoc-drop-after'));
        // Suppress the click the browser fires after a drag, which would
        // otherwise open the viewer on whatever was dropped.
        setTimeout(() => { dragId = null; }, 0);
      });
    });

    list.addEventListener('dragover', (e) => {
      if (dragId == null) return;
      const over = e.target.closest('[data-reorder-id]');
      if (!over || over.dataset.reorderId === dragId) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      const box = over.getBoundingClientRect();
      const after = (e.clientY - box.top) > box.height / 2;
      list.querySelectorAll('.isgdoc-drop-before, .isgdoc-drop-after')
        .forEach((n) => n.classList.remove('isgdoc-drop-before', 'isgdoc-drop-after'));
      over.classList.add(after ? 'isgdoc-drop-after' : 'isgdoc-drop-before');
    });

    list.addEventListener('drop', async (e) => {
      if (dragId == null) return;
      const over = e.target.closest('[data-reorder-id]');
      if (!over || over.dataset.reorderId === dragId) return;
      e.preventDefault();
      const box = over.getBoundingClientRect();
      const after = (e.clientY - box.top) > box.height / 2;
      await commitReorder(dragId, over.dataset.reorderId, after);
    });
  }

  async function commitReorder(movedId, targetId, after) {
    // The ids currently on screen, in the order they are drawn. This is the
    // whole folder — `canReorder` guarantees no search filter is narrowing
    // it, so nothing invisible is being renumbered.
    const list = container.querySelector('#isgdoc-doclist');
    const ids = Array.from(list.querySelectorAll('[data-reorder-id]'))
      .map((n) => n.dataset.reorderId);
    const from = ids.indexOf(movedId);
    if (from === -1) return;
    ids.splice(from, 1);
    const at = ids.indexOf(targetId);
    if (at === -1) return;
    ids.splice(after ? at + 1 : at, 0, movedId);

    // Optimistic: reorder the local rows so the list settles immediately,
    // then persist. A failure re-reads from the server and puts it back.
    const rank = new Map(ids.map((id, i) => [String(id), i]));
    documents.forEach((d) => {
      const r = rank.get(String(d.id));
      if (r !== undefined) d.sort_order = r;
    });
    documents.sort((a, b) => (a.sort_order - b.sort_order));
    render();

    try {
      await api.put(`${apiBase}/reorder`, { ids: ids.map((i) => Number(i)) });
    } catch (err) {
      // i18n-ignore: `action` is an English identifier for the app's onError hook; the reader sees the translated message
      fail(err, 'save the new order', t('docs.fail_reorder'));
      await loadDocuments();
      render();
    }
  }

  async function loadDocuments() {
    try {
      // listPath exists because some apps paginate the admin list and need
      // a per_page big enough that folder counts are the real totals.
      const rows = await api.get(cfg.listPath || apiBase);
      documents = Array.isArray(rows) ? rows : (rows.documents || []);
    } catch (err) {
      documents = [];
      // i18n-ignore: `action` is an English identifier for the app's onError hook; the reader sees the translated message
      fail(err, 'load documents', t('docs.fail_load'));
    }
  }

  async function loadFolders() {
    try {
      // foldersListPath is separate from foldersBase because a query string
      // (marketing's ?all=1 for inactive folders) cannot be appended to the
      // CRUD base — `${base}/${id}` would then interpolate mid-query.
      const rows = await api.get(cfg.foldersListPath || foldersBase);
      folders = Array.isArray(rows) ? rows : (rows.categories || []);
    } catch (_) { /* keep what we had */ }
  }

  async function loadLookups() {
    if (!features.conditions) return;
    // Some apps expose ONE endpoint returning {companies, countries,
    // departments} (marketing's /documents/lookups) rather than two.
    if (lookups.combined) {
      const all = await api.get(lookups.combined).catch(() => ({}));
      companies = all.companies || [];
      // Departments arrive as bare name strings there, objects elsewhere.
      departments = (all.departments || []).map((d) => (
        typeof d === 'string' ? { id: d, name: d } : d));
      return;
    }
    const [c, d] = await Promise.all([
      lookups.companies ? api.get(lookups.companies).catch(() => []) : [],
      lookups.departments ? api.get(lookups.departments).catch(() => []) : [],
    ]);
    companies = c || [];
    departments = d || [];
  }

  async function refresh() {
    // Folders FIRST — cards, labels and the folder dropdown all read them,
    // so painting documents before folders shows raw slugs.
    await loadFolders();
    await Promise.all([loadDocuments(), loadLookups()]);
    loading = false;
    render();
  }

  render();           // loading state
  refresh();

  return {
    refresh,
    destroy() {
      destroyed = true;
      document.removeEventListener('keydown', escHandler);
      container.innerHTML = '';
    },
    get state() {
      return { documents, folders, currentFolder };
    },
  };
}
